/**
 * Copyright 2016 Taito United Oy
 * All rights reserved.
 */

import PropTypes from 'prop-types';
import queryString from 'query-string';
import React from 'react';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import styled from 'styled-components';
import { RP_LOGO_URL, getApiUrl } from '~services/utils'; // eslint-disable-line

import Box, { Minifiable } from '../../components/Box';
import Button from '../../components/Button';
import Header from '../../components/Header';
import LoadingIndicator from '../common/LoadingIndicator';

import messages from './messages/QuickCheck_messages';

const propTypes = {
  fetchCLReport: PropTypes.func.isRequired,
  intl: intlShape.isRequired,
  quickCheck: PropTypes.object,
  session: PropTypes.object.isRequired,
};

const defaultProps = {
  quickCheck: undefined,
};

const QuickCheck = ({
  fetchCLReport,
  intl,
  quickCheck,
  session,
}) => {
  // Check login-status
  let loggedIn = false;
  if (session.logins) {
    const { logins } = session;
    if (logins.tilaajavastuu) {
      const { tilaajavastuu } = logins;
      if (tilaajavastuu.client) {
        loggedIn = true;
      }
    }
  }

  // Generate redirect url after successful Gluu login
  const { origin, pathname, search } = window.location;
  const parsedQuery = queryString.parse(search);
  parsedQuery.section = 'quickcheck';
  const postGluuLoginRedirectUrl = (
    `${origin}${pathname}?${queryString.stringify(parsedQuery)}`
  );
  const sessionParams = queryString.stringify({
    locale: intl.locale,
    postGluuLoginRedirectUrl,
  });
  const loginUrl = `${getApiUrl()}/authenticate/login?${sessionParams}`;

  // Fetch the full contractor's liability report
  const fetchReport = evnt => {
    evnt.preventDefault();
    const {
      company: {
        countryCode: country,
        businessId: identifier,
      },
    } = quickCheck;
    fetchCLReport({ country, identifier, format: 'pdf' });
  }

  // Generate capitalized locale to use when accessing objects properties
  const locale = intl.locale.charAt(0).toUpperCase() + intl.locale.slice(1);

  return (
    <Box
      headerLabel={<FormattedMessage { ...messages.titleShort } />}
      id="statement-section-quickcheck"
    >
      {(quickCheck === undefined) && (
        <FillWrap>
          <LoadingIndicator />
        </FillWrap>
      )}
      {(quickCheck === null) && (
        <FillWrap>
          <FormattedMessage { ...messages.notAvailable } />
        </FillWrap>
      )}
      {quickCheck && (
        <React.Fragment>
          <RPLogo
            src={RP_LOGO_URL}
            alt={intl.formatMessage({ ...messages.rpLogo })}
          />

          <Header>
            <FormattedMessage { ...messages.title } />
          </Header>

          <Disclaimer>
            <Info>
              <FormattedMessage { ...messages.disclaimer } />
              <FormattedMessage { ...messages.downloadInfo } />
            </Info>
            {loggedIn && (
              <Button badge onClick={fetchReport} color='green'>
                <FormattedMessage { ...messages.downloadButtonLabel } />
              </Button>
            )}
            {!loggedIn && (
              <Button as='a' badge href={loginUrl} color='green'>
                <FormattedMessage { ...messages.loginButtonLabel } />
              </Button>
            )}
          </Disclaimer>

          <Header underline>
            { quickCheck.company.name } ({ quickCheck.company.businessId })
          </Header>

          <Table>
            <tbody>
              <Row>
                <Cell label>
                  <FormattedMessage { ...messages.companyForm } />
                </Cell>
                <Cell>
                  <FormattedMessage
                    id={`companyForm.${ quickCheck.company.companyForm }`}
                  />
                </Cell>
              </Row>
              <Row>
                <Cell label>
                  <FormattedMessage { ...messages.companyHometown } />
                </Cell>
                <Cell>{ quickCheck.company.hometown[`name${locale}`] }</Cell>
              </Row>
              <Row>
                <Cell label>
                  <FormattedMessage { ...messages.companyIndustry } />
                </Cell>
                <Cell>{ quickCheck.company.industry[`name${locale}`] }</Cell>
              </Row>
            </tbody>
          </Table>

          <Interpretation>
            <StatusIcon
              alt={intl.formatMessage(
                { ...messages[`statusName_${ quickCheck.rpStatusId }`] }
              )}
              src={`/selvitys/images/rp_${ quickCheck.rpStatusId }.png`}
            />
            <FormattedMessage
              { ...messages[`statusInfo_${ quickCheck.rpStatusId }`] }
            />
          </Interpretation>

          <Minifiable>
            <Header as='h2' underline>
              <FormattedMessage { ...messages.titleStatuses } />
            </Header>
            <StatusInfos>
              <div>
                <StatusInfo>
                  <StatusIcon
                    alt={intl.formatMessage({ ...messages.statusName_OK })}
                    small
                    src='/selvitys/images/rp_OK.png'
                  />
                  <FormattedMessage
                    { ...messages.statusInfo_OK }
                  />
                </StatusInfo>
                <StatusInfo>
                  <StatusIcon
                    alt={
                      intl.formatMessage({ ...messages.statusName_ATTENTION })
                    }
                    small
                    src='/selvitys/images/rp_ATTENTION.png'
                  />
                  <FormattedMessage
                    { ...messages.statusInfo_ATTENTION }
                  />
                </StatusInfo>
                <StatusInfo>
                  <StatusIcon
                    alt={
                      intl.formatMessage({ ...messages.statusName_INCOMPLETE })
                    }
                    small
                    src='/selvitys/images/rp_INCOMPLETE.png'
                  />
                  <FormattedMessage
                    { ...messages.statusInfo_INCOMPLETE }
                  />
                </StatusInfo>
              </div>
              <div>
                <StatusInfo>
                  <StatusIcon
                    alt={
                      intl.formatMessage({ ...messages.statusName_INVESTIGATE })
                    }
                    small
                    src='/selvitys/images/rp_INVESTIGATE.png'
                  />
                  <FormattedMessage
                    { ...messages.statusInfo_INVESTIGATE }
                  />
                </StatusInfo>
                <StatusInfo>
                  <StatusIcon
                    alt={intl.formatMessage({ ...messages.statusName_WARNING })}
                    small
                    src='/selvitys/images/rp_WARNING.png'
                  />
                  <FormattedMessage
                    { ...messages.statusInfo_WARNING }
                  />
                </StatusInfo>
              </div>
            </StatusInfos>
          </Minifiable>
        </React.Fragment>
      )}
    </Box>
  );
}

QuickCheck.propTypes = propTypes;
QuickCheck.defaultProps = defaultProps;

const FillWrap = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 320px;
  width: 100%;
`;

const RPLogo = styled.img`
  align-self: flex-start;
  max-width: 120px;
  width: 100%;
`;

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
  margin-bottom: 24px;
`;

const Row = styled.tr`
  border: 1px solid black;
`;

const Cell = styled.td`
  background: ${
    props => props.as === 'th'
      ? props.theme['grey-normal']
      : 'none'
    };
  border: 1px solid ${props => props.theme['grey-normal']};
  font-weight: ${
    props => props.label
      ? 'bold'
      : 'normal'
    };
  padding: 6px 12px;
  text-align: left;
`;

const Disclaimer = styled.div`
  align-items: center;
  background: ${props => props.theme.beige};
  border-radius: 6px;
  display: flex;
  justify-content: space-between;
  padding: 18px;
  flex: 1;

  @media (max-width: 1100px) {
    flex-direction: column;
    text-align: center;
  }
`;

const Info = styled.div`
  > * {
    display: block;
  }

  @media (max-width: 1100px) {
    margin-bottom: 18px;
  }
`;

const Interpretation = styled.div`
  display: flex;
  font-weight: bold;
  align-items: center;
`;

const StatusIcon = styled.img`
  margin-right: 12px;
  max-width: ${
    props => props.small
      ? '50px'
      : '75px'
    };
`;

const StatusInfos = styled.div`
  display: flex;
  flex-wrap: wrap;

  > * {
    width: 50%;

    @media (max-width: 1100px) {
      width: 100%;
    }
  }
`;

const StatusInfo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 6px;
`;

export default injectIntl(QuickCheck);
